import { useMemo } from 'react';
import { MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import {
  SearchFilterInput,
  type ActiveFilter,
  type FilterDefinition,
  type FilterSuggestion
} from '@oysterjs/ui/search-filter';
import type { WorkspaceUsers } from '@oysterjs/smartfill-contracts';
import { useSidebarSession } from '../bridge/session';
import type { WorkspaceUserSelection } from '../sidebar/data';

type WorkspaceFilterType = 'search' | 'user';

export interface WorkspaceSearchValue {
  query: string;
  selection: WorkspaceUserSelection;
}

// Wording matches the Cara workspace list's filters, whose definitions cannot be
// imported here because they fetch users through the web app's GraphQL client.
const searchFilter: FilterDefinition<WorkspaceFilterType> = {
  type: 'search',
  icon: MagnifyingGlassIcon,
  priority: 100,
  isUnique: false,
  generate: ({ query }) => [{ value: query, displayLabel: 'Search for', displayValue: query }],
  deserialize: (serialized) => ({
    id: `search-${serialized.value}`,
    type: 'search',
    value: serialized.value,
    displayLabel: 'Search for',
    displayValue: serialized.displayValue
  })
};

function userFilter(load: () => Promise<WorkspaceUsers>): FilterDefinition<WorkspaceFilterType> {
  let directory: Promise<WorkspaceUsers> | undefined;
  return {
    type: 'user',
    icon: UserIcon,
    priority: 10,
    isUnique: true,
    async generate({ query }) {
      directory ??= load();
      let users: WorkspaceUsers;
      try {
        users = await directory;
      } catch {
        directory = undefined;
        return [];
      }
      const needle = query.toLowerCase();
      const options: FilterSuggestion[] = [
        { value: 'me', displayLabel: 'User is', displayValue: 'Me' },
        { value: 'all', displayLabel: 'User is', displayValue: 'All users' },
        ...users.users
          .filter((user) => user.id !== users.currentUserId)
          .map((user) => ({
            value: user.id,
            displayLabel: 'User is',
            displayValue: user.name.trim() || user.email
          }))
      ];
      return options.filter(
        (option) => option.displayValue.toLowerCase().includes(needle) || 'user'.includes(needle)
      );
    },
    deserialize: (serialized) => ({
      id: `user-${serialized.value}`,
      type: 'user',
      value: serialized.value,
      displayLabel: 'User is',
      displayValue: serialized.displayValue
    })
  };
}

/** Without a user tag the list is the caller's own workspaces, as in Cara. */
function searchValue(filters: ActiveFilter<WorkspaceFilterType>[]): WorkspaceSearchValue {
  const query = filters
    .filter((filter) => filter.type === 'search')
    .map((filter) => filter.value)
    .join(' ')
    .trim();
  const user = filters.find((filter) => filter.type === 'user');
  const selection: WorkspaceUserSelection =
    !user || user.value === 'me' ? 'me' : user.value === 'all' ? 'all' : { userId: user.value };
  return { query, selection };
}

export function WorkspaceSearch({ onChange }: { onChange: (value: WorkspaceSearchValue) => void }) {
  const { client } = useSidebarSession();
  const definitions = useMemo(
    () => [userFilter(() => client.request('workspaces.users', {})), searchFilter],
    [client]
  );
  return (
    <div className="workspace-search">
      <SearchFilterInput
        filterDefinitions={definitions}
        ariaLabel="Search workspaces"
        size="compact"
        placeholder="Search workspaces or users"
        onFiltersChange={(filters) => onChange(searchValue(filters))}
      />
    </div>
  );
}
